import { Button } from '@hbs-us/ui';
import type { ReactNode } from 'react';

/**
 * Shared `_ops` block — contextual bulk-action bar (UI-SPEC §4 "Bulk actions").
 *
 * Appears when ≥1 row is selected; shows the selection count and a row of
 * contextual actions (e.g. "Approve 4 weeks", "Archive 3 items"). It is
 * ANNOUNCED when it appears via `role="status"` + `aria-live="polite"` (A11Y-01),
 * its actions are keyboard-reachable (`@hbs-us/ui` Button = 44px target), and a
 * "Clear selection" affordance is always present. Destructive actions are passed
 * in as `variant="destructive"` BulkActions; the CALLER routes them through
 * `_money/ConfirmDialog` before firing `onClick` (the bar never confirms itself).
 *
 * Composition primitive: it renders the passed count + actions, it does not
 * fetch or mutate. Every color is a locked token (no hex, BRAND-06); the
 * neutral `bg-card` background keeps accent reserved for the primary CTA.
 */

export interface BulkAction {
  /** Stable key for the action. */
  key: string;
  /** Visible label (a t() string in app code, e.g. "Approve 4 weeks"). */
  label: ReactNode;
  /** Fired on activation. Destructive actions confirm in the caller first. */
  onClick: () => void;
  /** Button variant — destructive renders the red destructive token. */
  variant?: 'primary' | 'secondary' | 'destructive';
  disabled?: boolean;
}

export function BulkActionBar({
  selectedCount,
  summaryLabel,
  clearLabel,
  onClear,
  actions,
}: {
  /** Number of selected rows. The bar renders only when this is ≥1. */
  selectedCount: number;
  /** The "N selected" summary text (locale-aware, built in app code). */
  summaryLabel: ReactNode;
  /** Accessible label for the clear-selection control. */
  clearLabel: string;
  /** Clears the current selection. */
  onClear: () => void;
  /** Contextual actions for the current selection. */
  actions: BulkAction[];
}) {
  if (selectedCount < 1) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-border bg-card px-4 py-3 shadow-sm"
    >
      <div className="flex items-center gap-3">
        <span className="font-semibold text-foreground text-sm">{summaryLabel}</span>
        <Button variant="ghost" size="sm" onClick={onClear} aria-label={clearLabel}>
          {clearLabel}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {actions.map((action) => (
          <Button
            key={action.key}
            variant={action.variant ?? 'secondary'}
            size="sm"
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
